import { createEmptyPolicy } from "../compOffPolicy/constants/defaultPolicy";
import { normalizeGeneralScopeFields } from "../compOffPolicy/utils/scopeSelectionUtils";

const parseIdArray = (value) => {
	if (value == null || value === "") return [];
	const normalize = (items) =>
		items.map((item) => {
			const num = Number(item);
			return Number.isNaN(num) ? item : num;
		});

	if (Array.isArray(value)) return normalize(value);
	if (typeof value === "string") {
		try {
			const parsed = JSON.parse(value);
			return Array.isArray(parsed) ? normalize(parsed) : [];
		} catch {
			return [];
		}
	}
	return [];
};

const CALC_TYPE_BY_ID = { 1: 1, 2: 2, 3: 3 };
const ROUND_TYPE_BY_ID = { 1: 1, 2: 2, 3: 3 };

/** API record → Redux comp-off policy shape */
export function mapComboApiToPolicy(record) {
	if (!record) return createEmptyPolicy();

	const defaults = createEmptyPolicy();
	const general = normalizeGeneralScopeFields({
		...defaults.general,
		offices: parseIdArray(record.OfficesIds),
		departments: parseIdArray(record.DepartmentIds),
		designations: parseIdArray(record.DesignationIds),
		employeeTypes: parseIdArray(record.EmployeeTypeIds),
		weeklyOffEligible: Boolean(record.IsWeeklyOffEligible),
		holidayEligible: Boolean(record.IsHolidayEligible),
		overtimeEligible: Boolean(record.IsOvertimeEligible),
		minHoursWorked: Number(record.MinHoursWorked ?? defaults.general.minHoursWorked),
		allowCombination: Boolean(record.IsAllowCombination),
		combineAcrossDays: Boolean(record.IsCombineAcrossDays),
		combinationWindowDays: Number(
			record.CombinationWindowDays ?? defaults.general.combinationWindowDays
		),
		minCombinedHours: Number(
			record.MinCombinedHours ?? defaults.general.minCombinedHours
		),
	});

	const calculationComboOffTypeId =
		record.CalculationComboOffTypeId ??
		defaults.calculation.calculationComboOffTypeId;
	const roundComboOffTypeId =
		record.RoundComboOffTypeId ?? defaults.calculation.roundComboOffTypeId;

	return {
		...defaults,
		general,
		calculation: {
			...defaults.calculation,
			calculationComboOffTypeId: CALC_TYPE_BY_ID[calculationComboOffTypeId]
				? calculationComboOffTypeId
				: 1,
			roundComboOffTypeId: ROUND_TYPE_BY_ID[roundComboOffTypeId]
				? roundComboOffTypeId
				: 1,
			roundHalfDay: Boolean(record.IsRoundHalfDay),
			maxCreditPerDay: Number(
				record.MaxCreditPerDay ?? defaults.calculation.maxCreditPerDay
			),
			maxCreditPerMonth: Number(
				record.MaxCreditPerMonth ?? defaults.calculation.maxCreditPerMonth
			),
		},
	};
}

/** Redux comp-off policy → LeavePolicyComboSetup body */
export function buildLeavePolicyComboPayload(leavePolicyId, policy) {
	const { general, calculation } = policy || createEmptyPolicy();

	return {
		LeavePolicyId: leavePolicyId,
		OfficesIds: general.offices || [],
		DepartmentIds: general.departments || [],
		DesignationIds: general.designations || [],
		EmployeeTypeIds: general.employeeTypes || [],
		IsWeeklyOffEligible: Boolean(general.weeklyOffEligible),
		IsHolidayEligible: Boolean(general.holidayEligible),
		IsOvertimeEligible: Boolean(general.overtimeEligible),
		MinHoursWorked: Number(general.minHoursWorked ?? 0),
		IsAllowCombination: Boolean(general.allowCombination),
		IsCombineAcrossDays: Boolean(general.combineAcrossDays),
		CombinationWindowDays: Number(general.combinationWindowDays ?? 7),
		MinCombinedHours: Number(general.minCombinedHours ?? 8),
		CalculationComboOffTypeId: Number(
			calculation.calculationComboOffTypeId ?? 1
		),
		IsRoundHalfDay: Boolean(calculation.roundHalfDay),
		RoundComboOffTypeId: Number(calculation.roundComboOffTypeId ?? 1),
		MaxCreditPerDay: Number(calculation.maxCreditPerDay ?? 0),
		MaxCreditPerMonth: Number(calculation.maxCreditPerMonth ?? 0),
		DataStatus: 1,
	};
}

export function extractLeavePolicyId(resp, currentId, editRecord) {
	return (
		currentId ||
		editRecord?.LeavePolicyId ||
		editRecord?.id ||
		resp?.payload?.data?.[0]?.LeavePolicyId ||
		resp?.payload?.data?.LeavePolicyId ||
		null
	);
}

const resolveNames = (ids, masterList, idKey, nameKey) => {
	const values = parseIdArray(ids);
	if (!values.length) return "-";
	return (
		values
			.map(
				(id) =>
					masterList?.find((item) => String(item[idKey]) === String(id))?.[
						nameKey
					] || id
			)
			.join(", ") || "-"
	);
};

const yesNo = (value) => (value ? "Yes" : "No");

/** Raw API record → read-only view rows */
export function buildComboViewDetails(rawRecord, masterData = {}) {
	if (!rawRecord) return [];

	return [
		{
			label: "Offices",
			value: resolveNames(
				rawRecord.OfficesIds,
				masterData.CompanyOfficeMaster,
				"CompanyOfficeId",
				"CompanyOfficeName"
			),
		},
		{
			label: "Departments",
			value: resolveNames(
				rawRecord.DepartmentIds,
				masterData.DepartmentMaster,
				"DepartmentId",
				"DepartmentName"
			),
		},
		{
			label: "Designations",
			value: resolveNames(
				rawRecord.DesignationIds,
				masterData.DesignationMaster,
				"DesignationId",
				"DesignationName"
			),
		},
		{
			label: "Employment Types",
			value: resolveNames(
				rawRecord.EmployeeTypeIds,
				masterData.EmploymentTypeMaster,
				"EmploymentTypeId",
				"EmploymentTypeName"
			),
		},
		{ label: "Weekly Off Eligible", value: yesNo(rawRecord.IsWeeklyOffEligible) },
		{ label: "Holiday Eligible", value: yesNo(rawRecord.IsHolidayEligible) },
		{ label: "Overtime Eligible", value: yesNo(rawRecord.IsOvertimeEligible) },
		{ label: "Minimum Hours Worked", value: rawRecord.MinHoursWorked ?? "-" },
		{ label: "Allow Combination", value: yesNo(rawRecord.IsAllowCombination) },
		{
			label: "Combine Across Days",
			value: yesNo(rawRecord.IsCombineAcrossDays),
		},
		{
			label: "Combination Window (days)",
			value: rawRecord.CombinationWindowDays ?? "-",
		},
		{ label: "Minimum Combined Hours", value: rawRecord.MinCombinedHours ?? "-" },
		{
			label: "Calculation Type",
			value:
				rawRecord.CalculationComboOffTypeName ||
				masterData.CalculationComboOffTypeMaster?.find(
					(t) =>
						String(t.CalculationComboOffTypeId) ===
						String(rawRecord.CalculationComboOffTypeId)
				)?.CalculationComboOffTypeName ||
				rawRecord.CalculationComboOffTypeId ||
				"-",
		},
		{ label: "Round Half Day", value: yesNo(rawRecord.IsRoundHalfDay) },
		{
			label: "Round Up Logic",
			value:
				rawRecord.RoundComboOffTypeName ||
				masterData.RoundComboOffTypeMaster?.find(
					(t) =>
						String(t.RoundComboOffTypeId) ===
						String(rawRecord.RoundComboOffTypeId)
				)?.RoundComboOffTypeName ||
				rawRecord.RoundComboOffTypeId ||
				"-",
		},
		{ label: "Max Credit Per Day", value: rawRecord.MaxCreditPerDay ?? "-" },
		{ label: "Max Credit Per Month", value: rawRecord.MaxCreditPerMonth ?? "-" },
	];
}
